import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BatteryWarning,
  Bell,
  CheckCircle2,
  Mail,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { iotApi, type IoTAlert } from '../../lib/iotApi';
import { useAuth } from '../contexts/AuthContext';

type LoadState = 'loading' | 'ready' | 'unavailable';

interface MetricCardProps {
  label: string;
  value: number | null;
  icon: React.ElementType;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-tertiary)]">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
          {value === null ? '—' : value}
        </p>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
      </div>
    </div>
  </div>
);

interface ChannelRowProps {
  title: string;
  description: string;
  status: string;
  icon: React.ElementType;
}

const ChannelRow: React.FC<ChannelRowProps> = ({ title, description, status, icon: Icon }) => (
  <div className="flex items-start gap-3 px-4 py-4 sm:px-5">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--gray-100)]">
      <Icon className="h-5 w-5 text-[var(--text-secondary)]" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-[var(--text-primary)]">{title}</p>
        <span className="rounded-full border border-[var(--border-light)] bg-[var(--gray-50)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]">
          {status}
        </span>
      </div>
      <p className="mt-1 text-sm text-[var(--text-secondary)]">{description}</p>
    </div>
  </div>
);

const SoverdiAlertSettingsR7: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [refreshEpoch, setRefreshEpoch] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoadState('loading');

    iotApi
      .getJson<IoTAlert[]>('/iot/alerts')
      .then((rows) => {
        if (!mounted) return;
        setAlerts(Array.isArray(rows) ? rows : []);
        setLoadState('ready');
      })
      .catch(() => {
        if (!mounted) return;
        setLoadState('unavailable');
      });

    return () => {
      mounted = false;
    };
  }, [refreshEpoch]);

  const summary = useMemo(() => {
    if (loadState !== 'ready') {
      return { open: null, acknowledged: null, total: null };
    }

    const open = alerts.filter((alert) => alert.status === 'open').length;
    return {
      open,
      acknowledged: alerts.length - open,
      total: alerts.length,
    };
  }, [alerts, loadState]);

  return (
    <div className="space-y-5 sm:space-y-6 pb-24 lg:pb-6" data-testid="alert-settings-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-settings')}
            className="inline-flex min-h-[40px] items-center gap-2 text-sm font-medium text-[var(--color-primary)]"
            data-testid="alert-settings-back"
          >
            <ArrowLeft className="h-4 w-4" /> Paramètres
          </button>
          <div className="mt-2 flex items-center gap-2 text-[var(--color-primary)]">
            <Bell className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Surveillance</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Alertes & notifications</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            État réel des alertes de l’exploitation et des canaux actuellement raccordés à Agri Safe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshEpoch((value) => value + 1)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-4 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--gray-50)]"
        >
          <RefreshCw className={`h-4 w-4 ${loadState === 'loading' ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard label="Alertes ouvertes" value={summary.open} icon={AlertTriangle} />
        <MetricCard label="Acquittées" value={summary.acknowledged} icon={CheckCircle2} />
        <MetricCard label="Total" value={summary.total} icon={Bell} />
      </div>

      {loadState === 'unavailable' && (
        <section
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
          role="status"
          data-testid="alert-settings-unavailable"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Alertes indisponibles</p>
            <p className="mt-1 text-sm">
              La lecture de l’API a échoué. Les compteurs restent indisponibles et ne sont jamais remplacés par zéro.
            </p>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
          <h2 className="font-semibold text-[var(--text-primary)]">Détection actuellement raccordée</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Cette liste décrit le moteur réellement actif aujourd’hui. Aucun seuil modifiable n’est simulé dans cette page.
          </p>
        </div>
        <div className="flex items-start gap-3 px-4 py-4 sm:px-5" data-testid="alert-rule-low-battery">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-warning)]/10">
            <BatteryWarning className="h-5 w-5 text-[var(--color-warning)]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-[var(--text-primary)]">Batterie faible</p>
              <span className="rounded-full bg-[var(--color-warning)]/10 px-2.5 py-1 text-xs font-semibold text-[var(--color-warning)]">
                Avertissement
              </span>
            </div>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Alerte générée par le backend lorsqu’un équipement remonte une tension batterie faible. La règle est actuellement gérée par Agri Safe.
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-4 sm:px-5">
          <h2 className="font-semibold text-[var(--text-primary)]">Canaux de notification</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Les alertes sont aujourd’hui consultables dans la plateforme. Les canaux externes ne sont pas présentés comme actifs tant qu’ils ne sont pas raccordés au moteur d’alertes.
          </p>
        </div>
        <div className="divide-y divide-[var(--border-light)]">
          <ChannelRow
            title="Dans la plateforme"
            description="Les événements réels sont disponibles dans la page Alertes avec leur état ouvert ou acquitté."
            status="Actif"
            icon={ShieldCheck}
          />
          <ChannelRow
            title="E-mail"
            description={`L’infrastructure e-mail existe pour d’autres services Agri Safe, mais elle n’est pas encore raccordée aux alertes${user?.email ? ` de ${user.email}` : ''}.`}
            status="Non raccordé"
            icon={Mail}
          />
          <ChannelRow
            title="Notification mobile"
            description="Aucun service push mobile n’est actuellement raccordé au moteur d’alertes client."
            status="Non raccordé"
            icon={Smartphone}
          />
          <ChannelRow
            title="SMS"
            description="Aucun fournisseur SMS n’est actuellement raccordé au moteur d’alertes client."
            status="Non raccordé"
            icon={MessageSquare}
          />
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold text-[var(--text-primary)]">Consulter les événements</p>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              La page Alertes reste la source opérationnelle pour consulter les événements et leurs états réels.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/r6-soverdi-alerts')}
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)] px-4 text-sm font-semibold text-white"
            data-testid="alert-settings-open-alerts"
          >
            Voir les alertes
          </button>
        </div>
      </section>
    </div>
  );
};

export default SoverdiAlertSettingsR7;
